import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

type Page = {
  emoji: string;
  ar: string;
  en: string;
  bg: string; // tailwind gradient classes
};

const PAGES: Page[] = [
  {
    emoji: "🐪",
    ar: "في الصحراء، عاش جملٌ طيّبٌ اسمه سامي.",
    en: "In the desert lived a kind little camel named Sami.",
    bg: "from-amber-200 via-orange-200 to-rose-200",
  },
  {
    emoji: "🐦",
    ar: "ذات يوم، وجد سامي عصفورًا صغيرًا تائهًا.",
    en: "One day, Sami found a tiny lost bird.",
    bg: "from-sky-200 via-cyan-200 to-emerald-200",
  },
  {
    emoji: "💛",
    ar: "أوصله إلى عائلته… لأنّ اللطف يصنع أصدقاء للأبد.",
    en: "He carried it home — because kindness makes friends forever.",
    bg: "from-rose-200 via-pink-200 to-amber-200",
  },
];

interface Props {
  lang: "ar" | "en";
}

/**
 * A small, emotional interactive story preview for the landing page.
 * - Bilingual pages (ar/en switch via prop)
 * - "Listen" button uses the browser's native SpeechSynthesis (no API needed,
 *   no network call, no key) — gracefully no-ops if unsupported.
 * - Soft entrance + page-turn motion via CSS transitions.
 */
export function StoryPreview({ lang }: Props) {
  const isAr = lang === "ar";
  const [page, setPage] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);

  const total = PAGES.length;
  const p = PAGES[page];
  const text = isAr ? p.ar : p.en;

  // Stop narration on unmount / page change
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [page, lang]);

  const speak = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = isAr ? "ar-SA" : "en-US";
    u.rate = isAr ? 0.85 : 0.95;
    u.pitch = 1.05;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const next = () => setPage((page + 1) % total);
  const prev = () => setPage((page - 1 + total) % total);

  return (
    <section
      aria-label={isAr ? "معاينة قصة تفاعلية" : "Interactive story preview"}
      className="px-5 py-20 bg-clay/10"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="font-display-en text-xs md:text-sm uppercase tracking-[0.3em] text-secondary font-bold mb-3 inline-flex items-center gap-2">
            <Sparkles className="size-3" aria-hidden="true" />
            {isAr ? "معاينة مجانية" : "Free preview"}
          </p>
          <h2
            className={`tracking-tight ${isAr ? "font-display-ar text-4xl md:text-5xl text-primary" : "font-display-en text-3xl md:text-4xl font-extrabold"}`}
            dir={isAr ? "rtl" : "ltr"}
          >
            {isAr ? "اقرأ معنا أولى قصصنا." : "Read our first story together."}
          </h2>
          <p
            className={`mt-3 max-w-xl mx-auto text-foreground/65 ${isAr ? "font-display-ar text-lg" : "text-base"}`}
            dir={isAr ? "rtl" : "ltr"}
          >
            {isAr
              ? "اضغط على زر الاستماع لتسمع القصة بصوت عربي حقيقي — كما يعيشها طفلك داخل التطبيق."
              : "Press play to hear the story narrated aloud — exactly how your child will experience it inside Jozori."}
          </p>
        </div>

        <div
          key={page}
          className={`relative mx-auto max-w-3xl rounded-[2.5rem] overflow-hidden bg-gradient-to-br ${p.bg} shadow-2xl ring-1 ring-foreground/5 animate-entrance`}
        >
          <div className="aspect-[4/3] md:aspect-[16/9] flex items-center justify-center p-8 md:p-14 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-30 mix-blend-soft-light"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.4), transparent 50%)",
              }}
            />
            <div className="relative text-center max-w-2xl">
              <div className="text-7xl md:text-8xl mb-6 animate-float" aria-hidden="true">
                {p.emoji}
              </div>
              <p
                key={`${page}-${lang}`}
                dir={isAr ? "rtl" : "ltr"}
                className={`text-foreground/90 leading-snug animate-entrance ${isAr ? "font-display-ar text-2xl md:text-3xl" : "font-display-en text-xl md:text-2xl font-bold"}`}
              >
                {text}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-card/90 backdrop-blur border-t border-clay/30 px-5 py-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={prev}
              aria-label={isAr ? "الصفحة السابقة" : "Previous page"}
              className="size-11 rounded-full bg-background border border-clay/40 inline-flex items-center justify-center hover:bg-clay/15 transition-colors"
            >
              {isAr ? <ChevronRight className="size-5" /> : <ChevronLeft className="size-5" />}
            </button>

            <button
              type="button"
              onClick={speak}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full font-bold text-sm shadow-press active:translate-y-0.5 active:shadow-none transition-all min-h-11"
            >
              {speaking ? (
                <>
                  <VolumeX className="size-4" aria-hidden="true" />
                  {isAr ? "إيقاف" : "Stop"}
                </>
              ) : (
                <>
                  <Volume2 className="size-4" aria-hidden="true" />
                  {isAr ? "استمع للقصة" : "Listen"}
                </>
              )}
            </button>

            <div
              className="flex items-center gap-1.5"
              aria-label={`${isAr ? "الصفحة" : "Page"} ${page + 1} ${isAr ? "من" : "of"} ${total}`}
            >
              {PAGES.map((_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className={`block h-1.5 rounded-full transition-all ${
                    i === page ? "w-6 bg-primary" : "w-1.5 bg-foreground/20"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              aria-label={isAr ? "الصفحة التالية" : "Next page"}
              className="size-11 rounded-full bg-background border border-clay/40 inline-flex items-center justify-center hover:bg-clay/15 transition-colors"
            >
              {isAr ? <ChevronLeft className="size-5" /> : <ChevronRight className="size-5" />}
            </button>
          </div>
        </div>

        <p
          className={`text-center mt-5 text-xs text-foreground/45 ${isAr ? "font-display-ar text-sm" : ""}`}
          dir={isAr ? "rtl" : "ltr"}
        >
          {isAr
            ? "صوت المعاينة من متصفّحك — الإصدار الكامل بأصوات بشريّة محترفة."
            : "Preview voice uses your browser's narrator — the full app ships with professionally recorded voice actors."}
        </p>
      </div>
    </section>
  );
}
